import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { cn } from '../../lib/utils';
import { NODE_TYPES, NODE_STATUSES } from './narrativeConstants';
import { Lock, GitBranch, Star, Sparkles } from 'lucide-react';

const NarrativeCustomNode = memo(({ data, selected }) => {
  const typeConfig = NODE_TYPES[data.node_type] || NODE_TYPES.encounter;
  const statusConfig = NODE_STATUSES[data.status] || NODE_STATUSES.not_started;
  const Icon = typeConfig.icon;
  const lang = data.language || 'fr';

  const outEdgeCount = data.outEdgeCount || 0;
  const isBranching = outEdgeCount > 1;
  const isDone = ['accomplished', 'failed', 'avoided'].includes(data.status);
  const isBlocked = data.status === 'blocked';
  const isSecret = data.is_secret;

  return (
    <div
      className={cn(
        'narrative-custom-node group relative cursor-pointer',
        'transition-all duration-300 ease-out',
        selected && 'z-50',
      )}
    >
      {/* Target handle — top */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-2.5 !h-2.5 !rounded-full !border-2 !border-card !-top-1.5 !bg-muted-foreground/60 group-hover:!bg-primary transition-colors"
      />

      {/* Glow ring on selection */}
      {selected && (
        <div
          className="absolute -inset-2 rounded-2xl opacity-40 blur-md animate-pulse pointer-events-none"
          style={{ background: `radial-gradient(circle, ${typeConfig.color}60 0%, transparent 70%)` }}
        />
      )}

      {/* Main card */}
      <div
        className={cn(
          'relative rounded-2xl overflow-hidden transition-all duration-300',
          'min-w-[170px] max-w-[210px]',
          'shadow-md hover:shadow-xl',
          selected && 'ring-2 ring-offset-2 ring-offset-background scale-[1.03]',
          isDone && 'opacity-80',
          isBlocked && 'opacity-50 saturate-50',
          isSecret && 'ring-1 ring-dashed',
        )}
        style={{
          borderColor: selected ? typeConfig.color : 'transparent',
          ringColor: selected ? typeConfig.color : undefined,
          background: 'hsl(var(--card))',
        }}
      >
        {/* Color top bar */}
        <div
          className="h-1 w-full"
          style={{ background: `linear-gradient(90deg, ${typeConfig.color}, ${typeConfig.color}80)` }}
        />

        {/* Inner content */}
        <div className="px-3 pb-2.5 pt-2 relative">
          {/* Type icon + Title row */}
          <div className="flex items-start gap-2">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
              style={{ background: typeConfig.color + '18' }}
            >
              <Icon className="h-3.5 w-3.5" style={{ color: typeConfig.color }} />
            </div>

            <div className="min-w-0 flex-1">
              <p className="font-cinzel font-bold text-[11px] leading-tight text-foreground line-clamp-2">
                {data.title}
              </p>
              {data.summary && (
                <p className="text-[9px] text-muted-foreground mt-0.5 line-clamp-2 leading-snug">
                  {data.summary}
                </p>
              )}
            </div>
          </div>

          {/* Bottom row: status + badges */}
          <div className="flex items-center gap-1 mt-2 flex-wrap">
            {/* Status pill */}
            <span
              className="inline-flex items-center gap-0.5 text-[8px] font-bold uppercase tracking-widest px-1.5 py-[2px] rounded-md"
              style={{
                background: statusConfig.color + '15',
                color: statusConfig.color,
                border: `1px solid ${statusConfig.color}30`,
              }}
            >
              <span
                className={cn(
                  'w-1.5 h-1.5 rounded-full shrink-0',
                  data.status === 'accomplished' && 'animate-pulse',
                )}
                style={{ background: statusConfig.color }}
              />
              {statusConfig.label[lang]}
            </span>

            {/* Act chip */}
            {data.act && (
              <span className="text-[8px] text-muted-foreground/80 bg-muted/60 px-1.5 py-[2px] rounded-md">
                {data.act}
              </span>
            )}

            {/* Trailing icons */}
            <div className="flex items-center gap-0.5 ml-auto">
              {isBranching && (
                <GitBranch className="h-3 w-3 text-amber-500" />
              )}
              {data.is_main_plot && (
                <Star className="h-3 w-3 text-primary fill-primary/30" />
              )}
              {isSecret && (
                <Lock className="h-3 w-3 text-slate-400" />
              )}
            </div>
          </div>
        </div>

        {/* Accomplished checkmark overlay */}
        {data.status === 'accomplished' && (
          <div className="absolute top-2 right-2">
            <Sparkles className="h-3 w-3 text-emerald-500/60" />
          </div>
        )}
      </div>

      {/* Source handle — bottom */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-2.5 !h-2.5 !rounded-full !border-2 !border-card !-bottom-1.5 !bg-muted-foreground/60 group-hover:!bg-primary transition-colors"
      />
    </div>
  );
});

NarrativeCustomNode.displayName = 'NarrativeCustomNode';

export default NarrativeCustomNode;
